import { useState, useEffect, useRef, useCallback } from 'react';
import conversationService from '../services/conversationService';
import groupSectionsByCategory from '../utils/groupSectionsByCategory';

/**
 * useParsedSections — Universal hook for ALL split views.
 *
 * Single source of truth for section data. Fetches from ParsedResponse (backend),
 * handles category grouping, streaming preview, and local edit state.
 *
 * Replaces the scattered pattern of:
 *   - useEffect + getLatestParsedResponse (added to 18 files individually)
 *   - useEffect + currentVersion.sections (version hook fallback)
 *   - Manual groupSectionsByCategory calls in rendering
 *
 * Usage:
 *   const { sections, groupedSections, isLoading, contentRef, refresh } = useParsedSections({
 *     conversation,
 *     isStreaming,
 *     streamingContent,
 *   });
 *
 * Returns:
 *   sections         — flat array of sections (for CRUD operations, validation, export)
 *   groupedSections  — [{category, categorySection, sections}] (for rendering with headers)
 *   isLoading        — true while fetching from backend
 *   contentRef       — ref holding raw markdown content (for export/save)
 *   refresh          — call to force re-fetch from backend
 *   setSections      — direct setter (for local edits before save)
 */
const useParsedSections = ({
  conversation,
  isStreaming = false,
  streamingContent = '',
  skipFetch = false, // For special cases like process-flows (mermaid diagrams)
}) => {
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Increment to force re-fetch
  const contentRef = useRef('');
  const lastFetchIdRef = useRef(null);
  const isLocalEditRef = useRef(false);

  const conversationId = conversation?._id || conversation?.id;
  const messageCount = conversation?.messages?.length || 0;

  // ── Fetch from ParsedResponse (backend source of truth) ──
  // Fallback chain: /parsed-responses/latest → conversation message parsed_content → empty
  const fetchSections = useCallback(async () => {
    if (!conversationId || skipFetch) return;

    // Don't re-fetch if local edit just happened (prevents overwriting user's changes)
    if (isLocalEditRef.current) {
      isLocalEditRef.current = false;
      return;
    }

    setIsLoading(true);
    try {
      // PRIMARY: Fetch from /parsed-responses/latest endpoint
      const parsed = await conversationService.getLatestParsedResponse(conversationId);

      if (parsed?.parsed_content?.sections?.length > 0) {
        const fetchedSections = parsed.parsed_content.sections;
        setSections(fetchedSections);

        // Build raw content for export
        contentRef.current = fetchedSections.map(s => {
          const c = s.rawContent || s.content || '';
          return c.trimStart().startsWith('#') ? c : `## ${s.title}\n\n${c}`;
        }).join('\n\n');

        lastFetchIdRef.current = parsed.id;
        return; // Got data from endpoint, done
      }

      // FALLBACK: If endpoint returned null/empty, check conversation messages
      // for any message that has parsed_content attached (saved by save_artifact tool)
      if (conversation?.messages?.length > 0) {
        // Find the last assistant message with parsed_content
        for (let i = conversation.messages.length - 1; i >= 0; i--) {
          const msg = conversation.messages[i];
          if (msg.role === 'assistant' && msg.parsed_content?.sections?.length > 0) {
            const fallbackSections = msg.parsed_content.sections;
            setSections(fallbackSections);
            contentRef.current = fallbackSections.map(s => {
              const c = s.rawContent || s.content || '';
              return c.trimStart().startsWith('#') ? c : `## ${s.title}\n\n${c}`;
            }).join('\n\n');
            console.log(`useParsedSections: fallback to message[${i}].parsed_content (${fallbackSections.length} sections)`);
            return;
          }
        }
      }
    } catch (err) {
      console.warn('useParsedSections: fetch failed', err);

      // ERROR FALLBACK: If endpoint failed, try conversation messages
      if (conversation?.messages?.length > 0) {
        for (let i = conversation.messages.length - 1; i >= 0; i--) {
          const msg = conversation.messages[i];
          if (msg.role === 'assistant' && msg.parsed_content?.sections?.length > 0) {
            setSections(msg.parsed_content.sections);
            console.log(`useParsedSections: error fallback to message[${i}].parsed_content`);
            return;
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, skipFetch]); // eslint-disable-line react-hooks/exhaustive-deps
  // NOTE: conversation intentionally excluded — it's a new object reference on
  // every render and would cause infinite re-fetch loops. The fallback to
  // conversation.messages uses the ref from the closure which is always current.

  // Fetch on mount + when conversation updates (new messages) + when refreshKey changes
  useEffect(() => {
    if (isStreaming) return; // Don't fetch during streaming
    fetchSections();
  }, [conversationId, messageCount, isStreaming, refreshKey, fetchSections]);

  // ── Grouped sections (memoized-ish via sections reference) ──
  const groupedSections = groupSectionsByCategory(sections);

  // ── Local edit support ──
  // Call this before setSections for local edits to prevent fetch overwriting
  const markLocalEdit = useCallback(() => {
    isLocalEditRef.current = true;
  }, []);

  // Force re-fetch (use after tool_use updates)
  const forceRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return {
    sections,
    setSections,
    groupedSections,
    isLoading,
    contentRef,
    refresh: fetchSections,
    forceRefresh,
    markLocalEdit,
  };
};

export default useParsedSections;
